import { z } from "zod";
import { bookTemplates } from "./catalog.js";
import { bookKindSchema, capabilityAvailabilitySchema, } from "./localization-accessibility.js";
const creationIdSchema = z
    .string()
    .min(2)
    .max(80)
    .regex(/^[A-Z][A-Z0-9_]+$/);
const creationTemplateIdSchema = z
    .string()
    .min(2)
    .max(100)
    .regex(/^[a-z][a-z0-9-]+$/);
export const creationProductionFamilySchema = z.enum([
    "NARRATIVE",
    "PICTURE_BOOK",
    "RECIPE",
    "COMIC_CORE",
]);
export const creationRegistrationSchema = z.enum(["CURRENT_CATALOG", "ROADMAP_ALIAS"]);
export const creationReadinessSchema = z.enum([
    "AVAILABLE_WORKFLOW",
    "SANDBOX_WORKFLOW",
    "CONTRACT_ONLY",
]);
export const creationEditionOverlaySchema = z.enum([
    "LIFE_STORY_CHRONOLOGICAL",
    "MOTHER_LEGACY",
    "FATHER_LEGACY",
    "RELATIONSHIP_SHARED_MEMOIR",
    "RELATIONSHIP_ROMANCE",
    "BEDTIME_READ_ALOUD",
    "FAMILY_CHRONICLE",
    "ANCESTRY_HERITAGE",
    "TRUE_STORY_ACTION",
    "HOLIDAY_TRADITIONS",
    "FAMILY_COOKBOOK",
    "COMIC_ORIGIN",
    "BABY_FIRST_YEAR",
    "WEDDING_SHARED_STORY",
    "TRAVEL_JOURNEY_NOTES",
    "TRIBUTE_MANY_VOICES",
]);
export const creationStageIdSchema = z.enum([
    "WELCOME",
    "DISCOVER",
    "CAPTURE",
    "SHAPE",
    "VISUALIZE",
    "APPROVE",
]);
export const creationInteractionSchema = z.enum([
    "GUIDED_CHOICE",
    "CONVERSATION",
    "UPLOAD",
    "CO_CREATION",
    "PREVIEW",
    "APPROVAL",
]);
export const creationStageSchema = z
    .object({
    id: creationStageIdSchema,
    label: z.string().min(3).max(80),
    invitation: z.string().min(12).max(240),
    outcome: z.string().min(8).max(180),
    interaction: creationInteractionSchema,
})
    .strict();
export const creationCaptureModeSchema = z.enum([
    "VOICE",
    "TEXT",
    "PHOTO",
    "DOCUMENT",
    "COLLABORATOR",
]);
export const creationCapturePrioritySchema = z
    .object({
    mode: creationCaptureModeSchema,
    priority: z.number().int().min(1).max(5),
    purpose: z.string().min(8).max(180),
})
    .strict();
export const creativeSelectionModeSchema = z.enum(["SINGLE", "MULTIPLE"]);
export const creativeChoiceSchema = z
    .object({
    id: creationIdSchema,
    label: z.string().min(2).max(60),
    description: z.string().min(8).max(180),
})
    .strict();
export const creativeDimensionSchema = z
    .object({
    id: creationIdSchema,
    label: z.string().min(2).max(80),
    prompt: z.string().min(8).max(200),
    selectionMode: creativeSelectionModeSchema,
    maxSelections: z.number().int().min(1).max(4),
    choices: z.array(creativeChoiceSchema).min(2).max(8),
})
    .strict()
    .superRefine((dimension, context) => {
    if (dimension.selectionMode === "SINGLE" && dimension.maxSelections !== 1)
        context.addIssue({
            code: "custom",
            path: ["maxSelections"],
            message: "Single-select dimensions must allow exactly one choice.",
        });
    if (dimension.maxSelections > dimension.choices.length)
        context.addIssue({
            code: "custom",
            path: ["maxSelections"],
            message: "The selection limit cannot exceed the available choices.",
        });
    const ids = dimension.choices.map((choice) => choice.id);
    if (new Set(ids).size !== ids.length)
        context.addIssue({
            code: "custom",
            path: ["choices"],
            message: "Choice IDs must be unique within a creative dimension.",
        });
});
export const creationImageRoleSchema = z.enum([
    "COVER",
    "PORTRAIT",
    "CHARACTER_REFERENCE",
    "SCENE",
    "MEMORY_ARTIFACT",
    "RECIPE_HERO",
    "RECIPE_STEP",
    "MILESTONE",
    "CEREMONY",
    "MAP",
    "LOCATION",
    "MEMORIAL",
    "COMIC_PANEL",
]);
export const creationImageTreatmentSchema = z.enum([
    "ORIGINAL_PHOTO",
    "RESTORED_PHOTO",
    "EDITORIAL_CROP",
    "COLLAGE",
    "ILLUSTRATED_LIKENESS",
    "FULL_ILLUSTRATION",
    "DOCUMENT_FACSIMILE",
    "MAP_GRAPHIC",
    "STEP_DIAGRAM",
    "SEQUENTIAL_ART",
]);
export const creationLikenessPolicySchema = z.enum([
    "NOT_APPLICABLE",
    "OWNER_CONFIRMATION",
    "SUBJECT_CONSENT",
    "GUARDIAN_ATTESTATION",
    "FAMILY_STEWARD_APPROVAL",
]);
export const creationImageRuleSchema = z
    .object({
    role: creationImageRoleSchema,
    treatments: z.array(creationImageTreatmentSchema).min(1).max(5),
    likenessPolicy: creationLikenessPolicySchema,
    provenanceRequired: z.boolean(),
    guidance: z.string().min(8).max(200),
})
    .strict();
export const creationFactScopeSchema = z.enum([
    "IDENTITY_DATE_PLACE",
    "QUOTED_MEMORY",
    "UNCERTAIN_MEMORY",
    "SENSITIVE_CLAIM",
    "CREATIVE_SCENE",
    "RECIPE_MEASUREMENT",
    "FOOD_SAFETY",
    "GEOGRAPHY_AND_ITINERARY",
    "LIKENESS",
]);
export const creationFactPolicySchema = z.enum([
    "REQUIRE_CONFIRMATION",
    "RECORD_AS_TOLD",
    "REQUIRE_ATTRIBUTION",
    "MARK_UNCERTAIN",
    "NO_INVENTION",
    "MARK_AS_CREATIVE",
    "REQUIRE_CONSENT",
]);
export const creationProvenanceLevelSchema = z.enum([
    "REQUIRED",
    "WHEN_AVAILABLE",
    "NOT_APPLICABLE",
]);
export const creationFactRuleSchema = z
    .object({
    scope: creationFactScopeSchema,
    policy: creationFactPolicySchema,
    provenance: creationProvenanceLevelSchema,
    instruction: z.string().min(12).max(240),
})
    .strict();
export const creationReviewGateIdSchema = z.enum([
    "NARRATOR_VOICE",
    "FACTS_AND_NAMES",
    "SOURCE_ATTRIBUTION",
    "RECIPE_ACCURACY",
    "FOOD_SAFETY",
    "GUARDIAN_AND_LIKENESS",
    "CO_CREATOR_ALIGNMENT",
    "GEOGRAPHY_AND_CULTURE",
    "SENSITIVE_CONTENT",
    "STORY_CONTINUITY",
    "READ_ALOUD",
    "IMAGE_RIGHTS",
    "PANEL_CONTINUITY",
    "FINAL_PROOF",
]);
export const creationReviewerSchema = z.enum([
    "OWNER",
    "SUBJECT",
    "GUARDIAN",
    "CO_CREATOR",
    "FAMILY_STEWARD",
    "EDITOR",
]);
export const creationReviewGateSchema = z
    .object({
    id: creationReviewGateIdSchema,
    label: z.string().min(3).max(100),
    reviewer: creationReviewerSchema,
    blocking: z.boolean(),
    instruction: z.string().min(12).max(220),
})
    .strict();
const uniqueBy = (values, key) => {
    const keys = values.map(key);
    return new Set(keys).size === keys.length;
};
export const bookCreationProfileSchema = z
    .object({
    version: z.literal(1),
    templateId: creationTemplateIdSchema,
    registration: creationRegistrationSchema,
    availability: capabilityAvailabilitySchema,
    readiness: creationReadinessSchema,
    bookKind: bookKindSchema.nullable(),
    productionFamily: creationProductionFamilySchema,
    editionOverlay: creationEditionOverlaySchema,
    itemLabel: z.string().min(2).max(40),
    itemLabelPlural: z.string().min(2).max(40),
    stages: z.array(creationStageSchema).length(6),
    capturePriorities: z.array(creationCapturePrioritySchema).min(2).max(5),
    creativeDimensions: z.array(creativeDimensionSchema).min(2).max(6),
    imageRules: z.array(creationImageRuleSchema).min(1).max(8),
    factRules: z.array(creationFactRuleSchema).min(2).max(9),
    reviewGates: z.array(creationReviewGateSchema).min(2).max(8),
})
    .strict()
    .superRefine((profile, context) => {
    const expectedStageIds = creationStageIdSchema.options;
    if (profile.stages.some((stage, index) => stage.id !== expectedStageIds[index]) ||
        !uniqueBy(profile.stages, (stage) => stage.id))
        context.addIssue({
            code: "custom",
            path: ["stages"],
            message: "Creation stages must contain the canonical six-stage journey in order.",
        });
    const priorities = profile.capturePriorities.map(({ priority }) => priority);
    if (priorities.some((priority, index) => priority !== index + 1) ||
        !uniqueBy(profile.capturePriorities, ({ mode }) => mode))
        context.addIssue({
            code: "custom",
            path: ["capturePriorities"],
            message: "Capture modes must be unique and use contiguous priority order.",
        });
    const uniqueCollections = [
        ["creativeDimensions", uniqueBy(profile.creativeDimensions, ({ id }) => id)],
        ["imageRules", uniqueBy(profile.imageRules, ({ role }) => role)],
        ["factRules", uniqueBy(profile.factRules, ({ scope }) => scope)],
        ["reviewGates", uniqueBy(profile.reviewGates, ({ id }) => id)],
    ];
    for (const [path, unique] of uniqueCollections)
        if (!unique)
            context.addIssue({ code: "custom", path: [path], message: `${path} IDs must be unique.` });
    const expectedReadiness = {
        WORKING: "AVAILABLE_WORKFLOW",
        SANDBOX: "SANDBOX_WORKFLOW",
        ROADMAP: "CONTRACT_ONLY",
    };
    if (profile.readiness !== expectedReadiness[profile.availability])
        context.addIssue({
            code: "custom",
            path: ["readiness"],
            message: "Readiness must agree with declared capability availability.",
        });
    if (profile.productionFamily === "COMIC_CORE" &&
        (profile.bookKind !== null || profile.editionOverlay !== "COMIC_ORIGIN"))
        context.addIssue({
            code: "custom",
            path: ["bookKind"],
            message: "Comic Core is a distinct experience and cannot masquerade as a BookKind.",
        });
    if (profile.editionOverlay.startsWith("RELATIONSHIP_") && profile.bookKind === "WEDDING")
        context.addIssue({
            code: "custom",
            path: ["bookKind"],
            message: "Relationship editions are not wedding editions.",
        });
});
const stage = (id, label, invitation, outcome, interaction) => ({ id, label, invitation, outcome, interaction });
const choice = (id, label, description) => ({ id, label, description });
const dimension = (id, label, prompt, choices, selectionMode = "SINGLE", maxSelections = 1) => ({ id, label, prompt, selectionMode, maxSelections, choices });
const imageRule = (role, treatments, likenessPolicy, provenanceRequired, guidance) => ({ role, treatments, likenessPolicy, provenanceRequired, guidance });
const factRule = (scope, policy, provenance, instruction) => ({ scope, policy, provenance, instruction });
const reviewGate = (id, label, reviewer, instruction, blocking = true) => ({ id, label, reviewer, blocking, instruction });
const narrativeCapture = [
    {
        mode: "VOICE",
        priority: 1,
        purpose: "Preserve cadence, vocabulary, emotion, and firsthand memory.",
    },
    {
        mode: "PHOTO",
        priority: 2,
        purpose: "Anchor people, eras, locations, and meaningful objects visually.",
    },
    {
        mode: "DOCUMENT",
        priority: 3,
        purpose: "Support names, dates, letters, records, and chronology.",
    },
    {
        mode: "COLLABORATOR",
        priority: 4,
        purpose: "Add another perspective without losing attribution.",
    },
    {
        mode: "TEXT",
        priority: 5,
        purpose: "Correct details and add memories when speaking is not practical.",
    },
];
const narrativeImages = [
    imageRule("PORTRAIT", ["ORIGINAL_PHOTO", "RESTORED_PHOTO", "EDITORIAL_CROP"], "SUBJECT_CONSENT", true, "Keep identity faithful and attach the source asset to every edited portrait."),
    imageRule("MEMORY_ARTIFACT", ["ORIGINAL_PHOTO", "RESTORED_PHOTO", "DOCUMENT_FACSIMILE", "COLLAGE"], "OWNER_CONFIRMATION", true, "Preserve the artifact's context, ownership, and caption."),
    imageRule("COVER", ["ORIGINAL_PHOTO", "EDITORIAL_CROP", "COLLAGE"], "SUBJECT_CONSENT", true, "Use only approved photographs and an owner-reviewed cover treatment."),
];
const narrativeFacts = [
    factRule("IDENTITY_DATE_PLACE", "REQUIRE_CONFIRMATION", "REQUIRED", "Confirm names, dates, relationships, and locations before final proof."),
    factRule("QUOTED_MEMORY", "RECORD_AS_TOLD", "REQUIRED", "Keep the speaker attached to direct recollections and quotations."),
    factRule("UNCERTAIN_MEMORY", "MARK_UNCERTAIN", "WHEN_AVAILABLE", "Retain honest uncertainty instead of converting recollection into fact."),
    factRule("CREATIVE_SCENE", "NO_INVENTION", "NOT_APPLICABLE", "Literary treatment may connect verified moments but may not invent events."),
    factRule("SENSITIVE_CLAIM", "REQUIRE_CONFIRMATION", "REQUIRED", "Require deliberate review for allegations, health details, and private conflict."),
];
const narrativeGates = [
    reviewGate("NARRATOR_VOICE", "This still sounds like the storyteller", "SUBJECT", "Approve voice, point of view, and handling of quoted memories."),
    reviewGate("FACTS_AND_NAMES", "People, dates, and places are right", "OWNER", "Resolve flagged identity and chronology questions."),
    reviewGate("SENSITIVE_CONTENT", "Private material is intentional", "OWNER", "Confirm every retained sensitive passage is appropriate to publish."),
    reviewGate("IMAGE_RIGHTS", "Every image belongs here", "OWNER", "Confirm rights, captions, likeness permission, and source attribution."),
    reviewGate("FINAL_PROOF", "The whole book is ready", "OWNER", "Approve the complete ordered preview and print proof."),
];
const storyShape = dimension("STORY_SHAPE", "Story shape", "What structure best serves this edition?", [
    choice("CHRONOLOGICAL", "Through the years", "Move from earliest memories toward the present."),
    choice("THEMATIC", "By the threads", "Organize around family, work, love, place, and values."),
    choice("TURNING_POINTS", "Turning points", "Build around decisions and moments of change."),
]);
const narrativeTreatment = dimension("LITERARY_TREATMENT", "Telling style", "How much literary texture should shape the true account?", [
    choice("PLAIN_SPOKEN", "Plainspoken", "Keep the prose direct, intimate, and close to the recording."),
    choice("REFLECTIVE", "Reflective", "Give memories room for meaning and hindsight."),
    choice("CINEMATIC_TRUE", "Cinematic but true", "Heighten pace and detail without inventing events."),
]);
const narrativeStages = (welcomeLabel, welcomeInvitation, discoverLabel, shapeLabel) => [
    stage("WELCOME", welcomeLabel, welcomeInvitation, "A clear storyteller, audience, and promise for the edition.", "GUIDED_CHOICE"),
    stage("DISCOVER", discoverLabel, "Trace the people, places, eras, and turning points worth returning to.", "A bounded map of chapters and memory threads.", "CONVERSATION"),
    stage("CAPTURE", "Tell it in your voice", "Speak naturally while the guide listens for details only you can give.", "Attributed memories, source material, and unresolved questions.", "CONVERSATION"),
    stage("SHAPE", shapeLabel, "Arrange true moments without sanding away the storyteller's voice.", "A complete structure with attribution and uncertainty visible.", "CO_CREATION"),
    stage("VISUALIZE", "See the memories on the page", "Place photographs and keepsakes where they deepen the account.", "A rights-aware visual narrative and complete preview.", "PREVIEW"),
    stage("APPROVE", "Give the final word", "Read, correct, and approve the account before it becomes permanent.", "An owner-approved manuscript and print proof.", "APPROVAL"),
];
const defineProfile = (profile) => bookCreationProfileSchema.parse(profile);
const narrativeProfile = (input) => defineProfile({
    version: 1,
    productionFamily: "NARRATIVE",
    itemLabel: "chapter",
    itemLabelPlural: "chapters",
    capturePriorities: [...narrativeCapture],
    imageRules: narrativeImages,
    factRules: narrativeFacts,
    reviewGates: narrativeGates,
    ...input,
});
const currentNarrativeProfile = (templateId, editionOverlay, welcomeLabel, welcomeInvitation, discoverLabel, shapeLabel, editionDimension) => narrativeProfile({
    templateId,
    registration: "CURRENT_CATALOG",
    availability: "WORKING",
    readiness: "AVAILABLE_WORKFLOW",
    bookKind: "LIFE_STORY",
    editionOverlay,
    stages: narrativeStages(welcomeLabel, welcomeInvitation, discoverLabel, shapeLabel),
    creativeDimensions: [editionDimension, storyShape, narrativeTreatment],
});
const currentProfiles = [
    currentNarrativeProfile("story", "LIFE_STORY_CHRONOLOGICAL", "Open the memory door", "Choose whose voice will lead and what this life should preserve.", "Follow the life threads", "Find the shape of your story", dimension("NARRATIVE_VOICE", "Narrative voice", "How should the storyteller feel on the page?", [
        choice("FIRST_PERSON", "In my own words", "A close first-person account led by the storyteller's speech."),
        choice("WARM_BIOGRAPHY", "Warm biography", "A gentle third-person telling supported by direct memories."),
    ])),
    currentNarrativeProfile("mother", "MOTHER_LEGACY", "Meet the woman before Mom", "Choose the parts of her whole life—not only motherhood—that deserve the page.", "Follow her many selves", "Hold womanhood and motherhood together", dimension("MATERNAL_LENS", "Maternal lens", "Which truth should this edition protect?", [
        choice("HER_WHOLE_SELF", "Her whole self", "Balance childhood, identity, work, love, and motherhood."),
        choice("WHAT_SHE_GAVE", "What she gave", "Center care, sacrifice, tradition, and unseen labor."),
        choice("WHAT_SHE_CHOSE", "What she chose", "Center agency, change, ambition, and personal becoming."),
    ])),
    currentNarrativeProfile("dad", "FATHER_LEGACY", "Ask Dad the real version", "Invite the stories behind the advice, jokes, work, silence, and care.", "Find the stories behind the answers", "Make room for what went unsaid", dimension("PATERNAL_LENS", "Paternal lens", "What should readers understand about him?", [
        choice("LESSONS_AND_WORK", "Lessons and work", "Follow craft, responsibility, mistakes, and earned wisdom."),
        choice("FATHER_AND_PERSON", "Father and person", "Balance fatherhood with the life and identity around it."),
        choice("HUMOR_AND_HEART", "Humor and heart", "Let wit open the door to tenderness and truth."),
    ])),
    currentNarrativeProfile("family", "FAMILY_CHRONICLE", "Gather the family around", "Choose which branches, generations, and shared identity this chronicle will hold.", "Trace the branches and bonds", "Weave many voices into one chronicle", dimension("FAMILY_CENTER", "Family center", "What gives this family its shape?", [
        choice("GENERATIONS", "Generations", "Follow inheritance and change from elders to descendants."),
        choice("HOME_AND_PLACE", "Home and place", "Center houses, migrations, neighborhoods, and gathering places."),
        choice("TRADITIONS", "Traditions", "Center rituals, recipes, sayings, and shared ways of life."),
    ])),
    currentNarrativeProfile("ancestry", "ANCESTRY_HERITAGE", "Begin where the records fade", "Choose the family line, migration, or heritage question this origin story will follow.", "Separate records, memories, and legends", "Build the origin story with visible evidence", dimension("HERITAGE_EVIDENCE", "Heritage lens", "How should sources and family tradition work together?", [
        choice("DOCUMENT_LED", "Document-led", "Let records anchor the chronology and claims."),
        choice("ORAL_HISTORY", "Oral history", "Center attributed family testimony and inherited stories."),
        choice("MIGRATION_STORY", "Migration story", "Follow departure, movement, settlement, and cultural continuity."),
    ])),
    currentNarrativeProfile("action", "TRUE_STORY_ACTION", "Cue the true-story opening", "Choose the real mission, stakes, and version of the hero this edition will reveal.", "Find the close calls and comebacks", "Cut the true events like an adventure", dimension("ACTION_TREATMENT", "Action treatment", "What drives the true-story momentum?", [
        choice("MISSION", "The mission", "Build around a concrete goal and what stood in its way."),
        choice("SURVIVAL", "Against the odds", "Build around danger, endurance, and recovery."),
        choice("COMEBACK", "The comeback", "Build around failure, adaptation, allies, and return."),
    ])),
    currentNarrativeProfile("holiday", "HOLIDAY_TRADITIONS", "Open the holiday box", "Choose the season, family circle, and traditions this edition should bring back.", "Follow the rituals through the years", "Arrange the season like a gathering", dimension("HOLIDAY_CENTER", "Holiday center", "What should carry the season across generations?", [
        choice("TRADITIONS", "Traditions", "Follow repeated rituals and how they changed."),
        choice("PEOPLE", "The people", "Center hosts, absences, reunions, and family roles."),
        choice("SENSE_MEMORY", "The feeling of it", "Center food, music, place, light, and sensory memory."),
    ])),
];
const relationshipCapture = [
    {
        mode: "VOICE",
        priority: 1,
        purpose: "Preserve both partners' cadence, emotion, and differing recollections.",
    },
    {
        mode: "COLLABORATOR",
        priority: 2,
        purpose: "Keep each partner and invited contributor explicitly attributed.",
    },
    {
        mode: "PHOTO",
        priority: 3,
        purpose: "Anchor shared eras, everyday rituals, places, and milestones.",
    },
    {
        mode: "DOCUMENT",
        priority: 4,
        purpose: "Preserve letters, messages, vows, and keepsakes exactly.",
    },
    {
        mode: "TEXT",
        priority: 5,
        purpose: "Resolve spelling, chronology, credits, and private edits.",
    },
];
const relationshipStages = (welcome, discover, shape, approve) => [
    stage("WELCOME", welcome, "Choose the two voices, shared scope, and privacy boundary for this relationship story.", "A fair place for each partner's voice without implying a wedding.", "GUIDED_CHOICE"),
    stage("DISCOVER", discover, "Trace first impressions, rituals, turning points, separations, and growth.", "A two-perspective map of the relationship.", "CONVERSATION"),
    stage("CAPTURE", "Tell both sides", "Record memories separately or together and keep meaningful differences visible.", "Attributed partner accounts, quotations, media, and open questions.", "CO_CREATION"),
    stage("SHAPE", shape, "Bring two accounts into one arc without flattening their distinct voices.", "A balanced manuscript with perspective intact.", "CO_CREATION"),
    stage("VISUALIZE", "See the years together", "Sequence portraits, candids, places, and keepsakes so the book moves like memory.", "A rights-aware visual relationship history.", "PREVIEW"),
    stage("APPROVE", approve, "Let both partners approve shared passages, private details, images, and proof.", "A jointly reviewed relationship book.", "APPROVAL"),
];
const relationshipImages = [
    imageRule("PORTRAIT", ["ORIGINAL_PHOTO", "EDITORIAL_CROP", "RESTORED_PHOTO"], "SUBJECT_CONSENT", true, "Both subjects approve portraits, crops, captions, and placement."),
    imageRule("MEMORY_ARTIFACT", ["ORIGINAL_PHOTO", "DOCUMENT_FACSIMILE", "COLLAGE"], "OWNER_CONFIRMATION", true, "Preserve letters and messages without silently rewriting them."),
    imageRule("COVER", ["ORIGINAL_PHOTO", "EDITORIAL_CROP", "COLLAGE"], "SUBJECT_CONSENT", true, "Both partners approve the selected cover image and crop."),
];
const relationshipFacts = [...narrativeFacts];
const relationshipGates = [
    reviewGate("CO_CREATOR_ALIGNMENT", "Both voices belong", "CO_CREATOR", "Each partner approves shared passages and differing memories."),
    reviewGate("FACTS_AND_NAMES", "The people and timeline are right", "OWNER", "Resolve names, relationships, dates, places, and chronology."),
    reviewGate("SENSITIVE_CONTENT", "Private moments are intentional", "CO_CREATOR", "Confirm intimate details and conflict are appropriate to publish."),
    reviewGate("IMAGE_RIGHTS", "Images and keepsakes are cleared", "OWNER", "Confirm subject consent, source rights, captions, and credits."),
    reviewGate("FINAL_PROOF", "We approve the whole book", "CO_CREATOR", "Both partners approve the complete preview and print proof."),
];
const relationshipProfile = (templateId, overlay, stages, dimensions) => defineProfile({
    version: 1,
    templateId,
    registration: "CURRENT_CATALOG",
    availability: "WORKING",
    readiness: "AVAILABLE_WORKFLOW",
    bookKind: "LIFE_STORY",
    productionFamily: "NARRATIVE",
    editionOverlay: overlay,
    itemLabel: "chapter",
    itemLabelPlural: "chapters",
    stages,
    capturePriorities: [...relationshipCapture],
    creativeDimensions: dimensions,
    imageRules: relationshipImages,
    factRules: relationshipFacts,
    reviewGates: relationshipGates,
});
const relationshipProfiles = [
    relationshipProfile("love", "RELATIONSHIP_SHARED_MEMOIR", relationshipStages("Begin with two voices", "Find the moments between you", "Weave a shared memoir", "Give each other the final word"), [
        dimension("RELATIONSHIP_VOICE", "Relationship voice", "Who tells the shared history?", [
            choice("SHARED_WE", "Our shared we", "Use a unified first-person plural voice for agreed memories."),
            choice("TWO_VOICES", "Two voices", "Alternate named perspectives and preserve differences."),
        ]),
        dimension("SHARED_LIFE_ARC", "Shared-life arc", "What should organize the relationship memoir?", [
            choice("THROUGH_THE_YEARS", "Through the years", "Follow the relationship from meeting to the present."),
            choice("LIFE_WE_BUILT", "The life we built", "Center home, family, rituals, challenges, and growth."),
            choice("ORDINARY_MAGIC", "Ordinary magic", "Build around small recurring moments that define the bond."),
        ]),
    ]),
    relationshipProfile("lovestory", "RELATIONSHIP_ROMANCE", relationshipStages("Set the romantic scene", "Find the scenes that changed everything", "Shape the true romance", "Choose the final line together"), [
        dimension("ROMANCE_VOICE", "Romance voice", "How literary should the true romance feel?", [
            choice("DUAL_FIRST_PERSON", "Two hearts, two voices", "Alternate close first-person perspectives."),
            choice("ROMANTIC_NARRATOR", "Romantic narrator", "Use third-person narration supported by quotations."),
            choice("LETTER_FORM", "Letters across time", "Build the arc from direct addresses and correspondence."),
        ]),
        dimension("ROMANCE_ARC", "Romance arc", "Which true tension carries the story?", [
            choice("FINDING_EACH_OTHER", "Finding each other", "Center first impressions, obstacles, and recognition."),
            choice("CHOOSING_EACH_OTHER", "Choosing each other", "Center commitment, sacrifice, and decisive moments."),
            choice("STAYING_TOGETHER", "Staying together", "Center change, repair, resilience, and continuing love."),
        ]),
    ]),
];
const bedtimeStages = [
    stage("WELCOME", "Choose who steps into the moonlight", "Invite the child to choose the hero, listener, and feeling to carry into bedtime.", "A safe hero, audience, emotional promise, and guardian boundary.", "GUIDED_CHOICE"),
    stage("DISCOVER", "Whisper the secret ingredients", "Pick a beloved thing, surprising place, companion, and just-big-enough problem.", "A bounded palette of story ingredients with room for invention.", "CONVERSATION"),
    stage("CAPTURE", "Tell the story what only you know", "Speak a memory, silly phrase, sound, or brave choice and hear the story answer back.", "Child-led language, source memories, pronunciations, and choices.", "CONVERSATION"),
    stage("SHAPE", "Build the quest together", "Choose meaningful turns while the guide protects pace, safety, and the bedtime landing.", "A read-aloud manuscript with child agency and a coherent emotional arc.", "CO_CREATION"),
    stage("VISUALIZE", "Meet yourself inside the pictures", "Choose the art world and preview the hero, scenes, and cover before every spread is made.", "A consented character reference and continuity-ready visual system.", "PREVIEW"),
    stage("APPROVE", "Read it aloud and change the magic", "Let child and guardian react, revise, and approve the complete bedtime experience.", "A guardian-approved, read-aloud-tested story.", "APPROVAL"),
];
const bedtimeDimensions = [
    dimension("HERO_IDENTITY", "Who is the hero?", "Who should step into the story world tonight?", [
        choice("CHILD_HERO", "Me", "The child becomes the hero with guardian-approved likeness use."),
        choice("LOVED_ONE_HERO", "Someone I love", "A loved one leads with consented details."),
        choice("IMAGINED_HERO", "Someone brand new", "Invent a character with no real-person likeness."),
    ]),
    dimension("STORY_WORLD", "Where does the door open?", "Choose a world that invites wonder without overwhelming bedtime.", [
        choice("ENCHANTED_NATURE", "Enchanted nature", "Forests, oceans, gardens, clouds, and friendly wild places."),
        choice("COZY_IMPOSSIBLE", "Cozy impossible place", "A moon bakery, pillow city, or other gentle marvel."),
        choice("FAR_ADVENTURE", "A far adventure", "Space, hidden kingdoms, or a daring voyage."),
    ]),
    dimension("CHILD_AGENCY", "How should the child shape it?", "Give the child clear power at a few meaningful turns.", [
        choice("PICK_THE_COMPANION", "Choose a companion", "Let the child select who helps the hero."),
        choice("CHOOSE_THE_BRAVE_ACT", "Choose the brave act", "Let the child decide how the hero meets the challenge."),
        choice("INVENT_THE_MAGIC", "Invent the magic", "Let the child name a power, object, phrase, or transformation."),
    ], "MULTIPLE", 2),
    dimension("BEDTIME_PROMISE", "How should the ending feel?", "Choose the feeling the story returns to when the lights go low.", [
        choice("SAFE_AND_HELD", "Safe and held", "Land in belonging, reassurance, and closeness."),
        choice("BRAVE_INSIDE", "Brave inside", "Resolve fear through a choice the child can recognize."),
        choice("CURIOUS_TOMORROW", "Curious for tomorrow", "End with wonder settled into gentle anticipation."),
    ]),
];
const bedtimeProfile = (templateId, registration) => defineProfile({
    version: 1,
    templateId,
    registration,
    availability: "SANDBOX",
    readiness: "SANDBOX_WORKFLOW",
    bookKind: "BEDTIME",
    productionFamily: "PICTURE_BOOK",
    editionOverlay: "BEDTIME_READ_ALOUD",
    itemLabel: "spread",
    itemLabelPlural: "spreads",
    stages: bedtimeStages,
    capturePriorities: [
        {
            mode: "VOICE",
            priority: 1,
            purpose: "Make the child's words, humor, sounds, and pronunciations part of the story.",
        },
        {
            mode: "PHOTO",
            priority: 2,
            purpose: "Provide consented character, object, pet, and setting references.",
        },
        {
            mode: "TEXT",
            priority: 3,
            purpose: "Let a guardian set names, reading level, boundaries, and corrections.",
        },
        {
            mode: "COLLABORATOR",
            priority: 4,
            purpose: "Let child and guardian co-create without losing their roles.",
        },
    ],
    creativeDimensions: bedtimeDimensions,
    imageRules: [
        imageRule("CHARACTER_REFERENCE", ["ORIGINAL_PHOTO", "ILLUSTRATED_LIKENESS"], "GUARDIAN_ATTESTATION", true, "A guardian controls every minor reference and derived character anchor."),
        imageRule("SCENE", ["FULL_ILLUSTRATION", "ILLUSTRATED_LIKENESS"], "GUARDIAN_ATTESTATION", true, "Every scene follows the approved character anchor and safety boundary."),
        imageRule("COVER", ["FULL_ILLUSTRATION", "ILLUSTRATED_LIKENESS", "COLLAGE"], "GUARDIAN_ATTESTATION", true, "The guardian approves title, child likeness, and cover treatment."),
    ],
    factRules: [
        factRule("IDENTITY_DATE_PLACE", "REQUIRE_CONFIRMATION", "REQUIRED", "A guardian confirms every real person's name and identifying detail."),
        factRule("QUOTED_MEMORY", "RECORD_AS_TOLD", "REQUIRED", "Keep family memory attached to the person who supplied it."),
        factRule("CREATIVE_SCENE", "MARK_AS_CREATIVE", "NOT_APPLICABLE", "Fantasy remains visibly separate from claimed family history."),
        factRule("LIKENESS", "REQUIRE_CONSENT", "REQUIRED", "Require guardian attestation before a minor's photo informs a character."),
        factRule("SENSITIVE_CLAIM", "NO_INVENTION", "NOT_APPLICABLE", "Do not introduce trauma, diagnosis, conflict, or fear beyond guardian boundaries."),
    ],
    reviewGates: [
        reviewGate("GUARDIAN_AND_LIKENESS", "The people and boundaries are safe", "GUARDIAN", "Approve likeness references, names, privacy, age fit, and excluded themes."),
        reviewGate("STORY_CONTINUITY", "The hero and quest stay coherent", "EDITOR", "Check character, setting, object, emotional, and illustration continuity."),
        reviewGate("READ_ALOUD", "The story works out loud", "GUARDIAN", "Read for rhythm, pronunciation, length, energy, and bedtime landing."),
        reviewGate("FINAL_PROOF", "The magic is ready to keep", "GUARDIAN", "Approve every spread, image, title, dedication, and print proof."),
    ],
});
const cookbookProfile = (templateId, registration) => defineProfile({
    version: 1,
    templateId,
    registration,
    availability: registration === "CURRENT_CATALOG" ? "SANDBOX" : "ROADMAP",
    readiness: registration === "CURRENT_CATALOG" ? "SANDBOX_WORKFLOW" : "CONTRACT_ONLY",
    bookKind: "COOKBOOK",
    productionFamily: "RECIPE",
    editionOverlay: "FAMILY_COOKBOOK",
    itemLabel: "recipe",
    itemLabelPlural: "recipes",
    stages: [
        stage("WELCOME", "Step into the family kitchen", "Choose the cook, table, and food tradition this book will carry forward.", "A cook, audience, and culinary promise.", "GUIDED_CHOICE"),
        stage("DISCOVER", "Set the table of contents", "Gather signature dishes, celebrations, staples, and the people behind them.", "A balanced recipe list with missing categories exposed.", "CONVERSATION"),
        stage("CAPTURE", "Cook it out loud", "Tell each recipe as you really make it, including handfuls and hidden cues.", "Structured ingredients, steps, sensory cues, and stories.", "CONVERSATION"),
        stage("SHAPE", "Test every instruction", "Turn kitchen shorthand into repeatable recipes while preserving the cook's character.", "Consistent recipes ready for human verification.", "CO_CREATION"),
        stage("VISUALIZE", "Plate the pages", "Pair food, hands, kitchens, and heirlooms with their recipes.", "A practical and appetizing cookbook preview.", "PREVIEW"),
        stage("APPROVE", "Pass the taste test", "Verify quantities, sequence, safety notes, credits, and every page.", "A cook-approved and print-ready collection.", "APPROVAL"),
    ],
    capturePriorities: [
        {
            mode: "VOICE",
            priority: 1,
            purpose: "Reveal tacit technique, sensory cues, substitutions, and stories.",
        },
        {
            mode: "PHOTO",
            priority: 2,
            purpose: "Show finished dishes, critical steps, kitchens, and recipe cards.",
        },
        {
            mode: "TEXT",
            priority: 3,
            purpose: "Enter exact quantities, temperatures, timings, and corrections.",
        },
        {
            mode: "DOCUMENT",
            priority: 4,
            purpose: "Preserve handwritten recipes and original attribution.",
        },
    ],
    creativeDimensions: [
        dimension("COLLECTION_SHAPE", "Collection shape", "How should readers find their way through the recipes?", [
            choice("BY_COURSE", "By course", "Organize from starters through desserts."),
            choice("BY_OCCASION", "By occasion", "Group everyday meals, holidays, and celebrations."),
            choice("BY_PERSON", "By the cooks", "Give each contributing cook a distinct section."),
        ]),
        dimension("EDITORIAL_STYLE", "Page personality", "What should it feel like to cook from this book?", [
            choice("HEIRLOOM", "Heirloom", "Lead with stories, handwriting, and memory-rich imagery."),
            choice("PRACTICAL", "Kitchen practical", "Prioritize scannable steps, timings, and yields."),
            choice("CELEBRATION", "Celebration table", "Use generous food imagery and occasion-led stories."),
        ]),
    ],
    imageRules: [
        imageRule("RECIPE_HERO", ["ORIGINAL_PHOTO", "EDITORIAL_CROP"], "OWNER_CONFIRMATION", true, "The pictured dish must match the recipe and identify its source."),
        imageRule("RECIPE_STEP", ["ORIGINAL_PHOTO", "EDITORIAL_CROP", "STEP_DIAGRAM"], "OWNER_CONFIRMATION", true, "Use step imagery only where it clarifies a consequential technique."),
        imageRule("MEMORY_ARTIFACT", ["ORIGINAL_PHOTO", "DOCUMENT_FACSIMILE"], "OWNER_CONFIRMATION", true, "Keep handwriting, authorship, and recipe-card context visible."),
    ],
    factRules: [
        factRule("RECIPE_MEASUREMENT", "REQUIRE_CONFIRMATION", "REQUIRED", "A cook approves every quantity, unit, yield, temperature, and timing."),
        factRule("FOOD_SAFETY", "NO_INVENTION", "REQUIRED", "Do not infer safety, preservation, allergen, or storage guidance."),
        factRule("QUOTED_MEMORY", "REQUIRE_ATTRIBUTION", "REQUIRED", "Attach recipe stories and claimed origins to their contributor."),
    ],
    reviewGates: [
        reviewGate("RECIPE_ACCURACY", "Every recipe works as written", "OWNER", "Approve ingredients, steps, yield, time, temperature, and substitutions."),
        reviewGate("FOOD_SAFETY", "Safety language is supported", "EDITOR", "Review any safety, preservation, allergen, or storage statement."),
        reviewGate("SOURCE_ATTRIBUTION", "Every dish credits its people", "FAMILY_STEWARD", "Confirm origin, contributor names, and variations."),
        reviewGate("FINAL_PROOF", "The cookbook is kitchen-ready", "OWNER", "Approve the complete preview and print proof."),
    ],
});
const comicProfile = defineProfile({
    version: 1,
    templateId: "comic",
    registration: "CURRENT_CATALOG",
    availability: "SANDBOX",
    readiness: "SANDBOX_WORKFLOW",
    bookKind: null,
    productionFamily: "COMIC_CORE",
    editionOverlay: "COMIC_ORIGIN",
    itemLabel: "page",
    itemLabelPlural: "pages",
    stages: [
        stage("WELCOME", "Choose the issue's hero", "Define the real person, audience, visual identity, and truth boundary for Issue One.", "A consented protagonist and explicit true-story contract.", "GUIDED_CHOICE"),
        stage("DISCOVER", "Find the origin and the nemesis", "Map powers, allies, obstacles, reversals, and the real mission still underway.", "A fact-grounded issue outline with dramatic beats.", "CONVERSATION"),
        stage("CAPTURE", "Record the scenes and sound effects", "Capture true dialogue, locations, actions, and the storyteller's comic energy.", "Attributed scenes, quotations, references, and open facts.", "CONVERSATION"),
        stage("SHAPE", "Script the panels", "Turn verified events into pages, panels, captions, balloons, and page turns.", "A panel script with pacing and continuity annotations.", "CO_CREATION"),
        stage("VISUALIZE", "Enter the sequential world", "Approve character anchors, costumes, environments, lettering, and panel language.", "A continuity-ready comic art system and page preview.", "PREVIEW"),
        stage("APPROVE", "Sign off on Issue One", "Review truth, likeness, balloon order, panel continuity, and print proof.", "An owner-approved comic issue contract.", "APPROVAL"),
    ],
    capturePriorities: [
        {
            mode: "VOICE",
            priority: 1,
            purpose: "Capture dialogue, timing, humor, action, and the storyteller's voice.",
        },
        {
            mode: "PHOTO",
            priority: 2,
            purpose: "Anchor character likeness, costume, props, and real settings.",
        },
        {
            mode: "DOCUMENT",
            priority: 3,
            purpose: "Support chronology, quotations, uniforms, and period detail.",
        },
        {
            mode: "TEXT",
            priority: 4,
            purpose: "Revise captions, dialogue, sound effects, and panel order precisely.",
        },
    ],
    creativeDimensions: [
        dimension("COMIC_TONE", "Comic tone", "What energy drives the issue?", [
            choice("PULP_ACTION", "Pulp action", "High contrast, bold stakes, and propulsive captions."),
            choice("HEROIC_WARMTH", "Heroic warmth", "Family-friendly scale with heart and humor."),
            choice("GRAPHIC_MEMOIR", "Graphic memoir", "Grounded observation, restraint, and emotional detail."),
        ]),
        dimension("PANEL_RHYTHM", "Panel rhythm", "How should time move across the page?", [
            choice("FAST_CUT", "Fast cut", "Short panels, hard transitions, and kinetic action."),
            choice("BALANCED", "Balanced", "Mix establishing scenes, dialogue, action, and reflection."),
            choice("CINEMATIC", "Cinematic", "Use wide beats, silent panels, and deliberate reveals."),
        ]),
        dimension("HERO_TREATMENT", "Hero treatment", "How far can true events be stylized?", [
            choice("TRUE_SCALE", "True scale", "Keep action and capability literal."),
            choice("VISUAL_METAPHOR", "Visual metaphor", "Use clearly figurative powers without changing facts."),
            choice("TALL_TALE_FRAME", "Tall-tale frame", "Bracket exaggeration explicitly as the storyteller's telling."),
        ]),
    ],
    imageRules: [
        imageRule("CHARACTER_REFERENCE", ["ORIGINAL_PHOTO", "ILLUSTRATED_LIKENESS"], "SUBJECT_CONSENT", true, "Approve the character turnaround before sequential art begins."),
        imageRule("COMIC_PANEL", ["SEQUENTIAL_ART", "ILLUSTRATED_LIKENESS"], "SUBJECT_CONSENT", true, "Panels follow the approved character, costume, setting, and action anchors."),
        imageRule("COVER", ["SEQUENTIAL_ART", "ILLUSTRATED_LIKENESS", "COLLAGE"], "SUBJECT_CONSENT", true, "Approve likeness, issue title, trade dress, and cover claims."),
    ],
    factRules: [
        ...narrativeFacts,
        factRule("LIKENESS", "REQUIRE_CONSENT", "REQUIRED", "Require subject consent for every real-person character transformation."),
    ],
    reviewGates: [
        reviewGate("FACTS_AND_NAMES", "The true-story spine is accurate", "OWNER", "Resolve names, chronology, locations, actions, and quotations."),
        reviewGate("GUARDIAN_AND_LIKENESS", "Every transformed likeness is approved", "OWNER", "Confirm subjects, references, character designs, and usage permission."),
        reviewGate("PANEL_CONTINUITY", "Panels read in order", "EDITOR", "Check balloons, eyelines, action, props, costume, and page turns."),
        reviewGate("FINAL_PROOF", "Issue One is ready", "OWNER", "Approve every page, panel, caption, credit, and print proof."),
    ],
});
const currentSpecializedProfiles = [
    bedtimeProfile("children", "CURRENT_CATALOG"),
    cookbookProfile("recipes", "CURRENT_CATALOG"),
    comicProfile,
];
const roadmapNarrativeAlias = narrativeProfile({
    templateId: "life-story-chronological",
    registration: "ROADMAP_ALIAS",
    availability: "WORKING",
    readiness: "AVAILABLE_WORKFLOW",
    bookKind: "LIFE_STORY",
    editionOverlay: "LIFE_STORY_CHRONOLOGICAL",
    stages: narrativeStages("Open the memory door", "Choose whose voice will lead and what this life should preserve.", "Follow the life threads", "Find the shape of your story"),
    creativeDimensions: [
        dimension("NARRATIVE_VOICE", "Narrative voice", "How should the storyteller feel on the page?", [
            choice("FIRST_PERSON", "In my own words", "A close first-person account led by the storyteller's speech."),
            choice("WARM_BIOGRAPHY", "Warm biography", "A gentle third-person telling supported by direct memories."),
        ]),
        storyShape,
        narrativeTreatment,
    ],
});
const compactRoadmapProfile = (templateId, bookKind, overlay, availability, welcome, discover, primaryDimension, imageRole, reviewer) => defineProfile({
    version: 1,
    templateId,
    registration: "ROADMAP_ALIAS",
    availability,
    readiness: availability === "ROADMAP" ? "CONTRACT_ONLY" : "SANDBOX_WORKFLOW",
    bookKind,
    productionFamily: "NARRATIVE",
    editionOverlay: overlay,
    itemLabel: "chapter",
    itemLabelPlural: "chapters",
    stages: [
        stage("WELCOME", welcome, "Choose the people, audience, scope, and boundaries for this future edition.", "A bounded purpose and participation plan.", "GUIDED_CHOICE"),
        stage("DISCOVER", discover, "Gather the moments, people, places, and artifacts that define the edition.", "A source-aware chapter map.", "CONVERSATION"),
        stage("CAPTURE", "Capture the voices and evidence", "Combine voice with images, documents, and attributed contributors.", "Attributed source material and visible gaps.", "CONVERSATION"),
        stage("SHAPE", "Shape the edition", "Arrange true material according to the selected creative grammar.", "A coherent draft without invented biographical fact.", "CO_CREATION"),
        stage("VISUALIZE", "Build the visual keepsake", "Place images and artifacts with consent, source, and context intact.", "A rights-aware preview.", "PREVIEW"),
        stage("APPROVE", "Give the final approval", "Resolve facts, permissions, sensitive content, and final proof.", "A reviewer-approved edition contract.", "APPROVAL"),
    ],
    capturePriorities: [...narrativeCapture],
    creativeDimensions: [primaryDimension, storyShape],
    imageRules: [
        imageRule(imageRole, ["ORIGINAL_PHOTO", "RESTORED_PHOTO", "EDITORIAL_CROP", "COLLAGE"], reviewer === "GUARDIAN"
            ? "GUARDIAN_ATTESTATION"
            : reviewer === "FAMILY_STEWARD"
                ? "FAMILY_STEWARD_APPROVAL"
                : "SUBJECT_CONSENT", true, "Approve source, treatment, caption, privacy, and placement before export."),
        imageRule("MEMORY_ARTIFACT", ["ORIGINAL_PHOTO", "DOCUMENT_FACSIMILE", "COLLAGE"], "OWNER_CONFIRMATION", true, "Keep the artifact linked to its source and contextual caption."),
    ],
    factRules: narrativeFacts,
    reviewGates: [
        reviewGate("FACTS_AND_NAMES", "People, places, and chronology are right", reviewer, "Resolve every flagged identity, date, location, and relationship."),
        reviewGate("SENSITIVE_CONTENT", "Private material is intentional", reviewer, "Confirm sensitive passages are appropriate to publish."),
        reviewGate("IMAGE_RIGHTS", "Every image is cleared", reviewer, "Confirm permission, source, caption, and treatment."),
        reviewGate("FINAL_PROOF", "The complete edition is approved", reviewer, "Approve the complete ordered preview and print proof."),
    ],
});
const roadmapProfiles = [
    roadmapNarrativeAlias,
    cookbookProfile("cookbook-family-recipes", "ROADMAP_ALIAS"),
    compactRoadmapProfile("baby-first-year", "BABY", "BABY_FIRST_YEAR", "ROADMAP", "Welcome, little one", "Gather the firsts", dimension("BABY_TIME_SCOPE", "Time window", "Which chapter of early life should this volume hold?", [
        choice("ARRIVAL", "Waiting and arrival", "Center pregnancy, adoption, birth, and coming home."),
        choice("FIRST_YEAR", "The first year", "Follow monthly growth and major firsts."),
        choice("EARLY_YEARS", "The early years", "Carry the story through toddlerhood."),
    ]), "MILESTONE", "GUARDIAN"),
    compactRoadmapProfile("wedding-shared-story", "WEDDING", "WEDDING_SHARED_STORY", "ROADMAP", "Begin with the ceremony promise", "Trace the day and its people", dimension("WEDDING_SCOPE", "Wedding scope", "Which part of the wedding experience leads?", [
        choice("CEREMONY_DAY", "The day itself", "Follow preparation, ceremony, celebration, and aftermath."),
        choice("PEOPLE_AND_VOWS", "People and vows", "Center promises, speeches, rituals, and community."),
        choice("JOURNEY_TO_DAY", "The journey to the day", "Connect planning and commitment to the celebration."),
    ]), "CEREMONY", "CO_CREATOR"),
    compactRoadmapProfile("travel-journey-notes", "TRAVEL", "TRAVEL_JOURNEY_NOTES", "ROADMAP", "Choose the road again", "Pin the map with memories", dimension("JOURNEY_SHAPE", "Journey shape", "How should readers travel through the book?", [
        choice("BY_ROUTE", "Follow the route", "Move through destinations in traveled order."),
        choice("BY_PLACE", "A chapter per place", "Give each destination its own portrait."),
        choice("BY_DISCOVERY", "By discovery", "Organize around people, food, nature, or challenge."),
    ]), "LOCATION", "OWNER"),
    compactRoadmapProfile("tribute-many-voices", "TRIBUTE", "TRIBUTE_MANY_VOICES", "SANDBOX", "Name the life we are honoring", "Gather what they gave us", dimension("TRIBUTE_VOICE", "Tribute voice", "How should the honored life be held on the page?", [
        choice("COLLECTIVE_VOICES", "A chorus of voices", "Let named family and friends speak distinctly."),
        choice("LEGACY_PORTRAIT", "A legacy portrait", "Use an editorial narrator supported by memories."),
        choice("PERSONAL_LETTERS", "Letters to them", "Organize messages and memories addressed to the person."),
    ]), "MEMORIAL", "FAMILY_STEWARD"),
    bedtimeProfile("bedtime-read-aloud", "ROADMAP_ALIAS"),
];
const allProfiles = [
    ...currentProfiles,
    ...relationshipProfiles,
    ...currentSpecializedProfiles,
    ...roadmapProfiles,
];
if (!uniqueBy(allProfiles, ({ templateId }) => templateId))
    throw new Error("Book creation template registry contains duplicate template IDs.");
const currentProfileIds = allProfiles
    .filter(({ registration }) => registration === "CURRENT_CATALOG")
    .map(({ templateId }) => templateId)
    .sort();
const catalogTemplateIds = bookTemplates.map(({ id }) => id).sort();
if (JSON.stringify(currentProfileIds) !== JSON.stringify(catalogTemplateIds))
    throw new Error("Book creation registry must match the current catalog exactly.");
export const currentBookCreationTemplateIds = Object.freeze(bookTemplates.map(({ id }) => id));
export const roadmapBookCreationTemplateIds = Object.freeze(roadmapProfiles.map(({ templateId }) => templateId));
export const bookCreationProfiles = Object.freeze(Object.fromEntries(allProfiles.map((profile) => [profile.templateId, Object.freeze(profile)])));
export function bookCreationProfileForTemplate(templateId) {
    return bookCreationProfiles[templateId];
}
export function bookCreationProfilesForBookKind(bookKind) {
    return allProfiles.filter((profile) => profile.bookKind === bookKind);
}
//# sourceMappingURL=creation-profile.js.map